/**
 * Process-local cache for the `mockData.json` sidecar plus the save-flow
 * helpers that depend on it.
 *
 * Why a cache: the save path reads the workflow identity synchronously and
 * lets the sidecar PUT run in parallel with the workflow PUT. The cache is
 * also what the draft↔published mode switch reads from to re-hydrate mocks
 * without a network roundtrip (cache-only, mirroring the layout sidecar).
 *
 * Unlike the layout sidecar this file is flat (draft-only), so there is no
 * "preserve the other branch" step — a plan simply snapshots the current
 * store slice. Always PUTting the current state means clearing all mocks
 * correctly persists an empty file.
 *
 * Lifecycle (not persisted across reloads — page load reseeds):
 *   - Page load: `setCachedMockData(key, fetched)`
 *   - Save success: `commitMockPlan(plan)` refreshes the cache
 *   - Workflow delete: `deleteCachedMockData(key)`
 *   - Sign-out: `clearMockCache()`
 */

import {
  buildMockData,
  type MockDataFile,
  type MockedNodesMap,
} from './mockData';
import {
  composeWorkflowKey,
  type WorkflowIdentity,
} from '@/stores/workflowViewPrefsStore';

const cache = new Map<string, MockDataFile>();

export function getCachedMockData(key: string): MockDataFile | undefined {
  return cache.get(key);
}

export function setCachedMockData(key: string, file: MockDataFile): void {
  cache.set(key, file);
}

export function deleteCachedMockData(key: string): void {
  cache.delete(key);
}

export function clearMockCache(): void {
  cache.clear();
}

/**
 * A pre-built sidecar write paired with its cache key. Save sites build a
 * plan, hand the `file` to `saveMockData`, and call {@link commitMockPlan} on
 * success to keep the cache in sync.
 */
export interface MockDataPlan {
  file: MockDataFile;
  key: string;
}

/** True when a slice carries no mock state worth persisting (content only). */
const sliceHasState = (
  mockedNodes: MockedNodesMap,
  mockRunEnabled: boolean,
): boolean => Object.keys(mockedNodes).length > 0 || mockRunEnabled;

/**
 * Build a sidecar write from the current store slice. Returns `null` when:
 *   - there's no workflow identity yet, or
 *   - the slice is empty AND nothing non-empty was previously persisted —
 *     i.e. there's nothing to write and nothing to clear. This stops every
 *     draft save on a never-had-mocks workflow from PUTting an empty sidecar.
 * When the slice is empty but the cache still holds mock state, a plan IS
 * returned so clearing all mocks correctly persists (and then clears) the file.
 */
export function planMockData(
  mockedNodes: MockedNodesMap,
  mockRunEnabled: boolean,
  identity: WorkflowIdentity | null,
): MockDataPlan | null {
  if (!identity) return null;
  const key = composeWorkflowKey(identity);
  if (!sliceHasState(mockedNodes, mockRunEnabled)) {
    const cached = getCachedMockData(key);
    const cachedHasState =
      !!cached && sliceHasState(cached.mockedNodes, cached.mockRunEnabled);
    if (!cachedHasState) return null;
  }
  const file = buildMockData(mockedNodes, mockRunEnabled);
  return { file, key };
}

/** Write-through: refresh the cache so the next mode switch reads fresh state. */
export function commitMockPlan(plan: MockDataPlan): void {
  setCachedMockData(plan.key, plan.file);
}
